/**
 * Challenge → firma ECDSA → campi extra per POST /api/auth/register.
 */
import {
  assertSha256Utf8Matches,
  exportPublicJwk,
  getOrCreateRegistrationSigningKeyPair,
  signCanonicalPayloadUtf8,
  signatureDerToStandardBase64,
} from './registrationWebCrypto.js';

/** Allinea data di nascita a UTC mezzanotte (stesso schema React Informazioni). */
export function dateOfBirthToApiIso(dateVal) {
  if (!dateVal) return '';
  const x = dateVal instanceof Date ? dateVal : new Date(dateVal);
  if (Number.isNaN(x.getTime())) return '';
  return new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate(), 0, 0, 0, 0)).toISOString();
}

/**
 * @param {object} opts
 * @param {string} [opts.apiBaseUrl='https://api.elitefitness.it']
 * @param {object} opts.challengePayload body JSON RegisterSigningChallengeRequest
 * @returns {Promise<{ signingCommitmentId: string, signingPublicKey: object, signingSignatureBase64: string, registrationAcceptances: object }>}
 */
export async function buildRegistrationSigningAttachments({ apiBaseUrl = 'https://api.elitefitness.it', challengePayload }) {
  const keys = await getOrCreateRegistrationSigningKeyPair();
  const url = `${apiBaseUrl.replace(/\/$/, '')}/api/auth/register/signing/challenge`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(challengePayload),
  });
  const text = await res.text();
  let json = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      /* noop */
    }
  }
  if (res.status === 429) {
    throw new Error(
      (json && (json.message || json.detail)) || 'Troppe richieste. Riprovare tra qualche minuto.',
    );
  }
  if (!res.ok) {
    const msg =
      (json && (json.message || json.detail || json.error)) ||
      text?.slice(0, 200) ||
      `Errore challenge (${res.status})`;
    throw new Error(msg);
  }
  const ch = json;
  await assertSha256Utf8Matches(ch.canonicalPayload, ch.contentSha256);
  const sigBuf = await signCanonicalPayloadUtf8(keys.privateKey, ch.canonicalPayload);
  const signingPublicKey = await exportPublicJwk(keys.publicKey);
  const signingSignatureBase64 = signatureDerToStandardBase64(sigBuf);
  return {
    signingCommitmentId: ch.commitmentId,
    signingPublicKey,
    signingSignatureBase64,
    registrationAcceptances: challengePayload.acceptances,
  };
}

/**
 * Firma le accettazioni legali per un socio GIÀ REGISTRATO e le invia al server.
 *
 * Perché esiste, accanto a buildRegistrationSigningAttachments: quella firma nel flusso di
 * registrazione, dove il server rifiuta un'email già in uso — che qui è la condizione normale.
 * Fino a oggi il socio già registrato spuntava le cinque caselle al checkout e non veniva salvato
 * niente: le spunte abilitavano il pulsante e l'accettazione spariva al clic.
 *
 * Due passaggi come nella registrazione: prima il challenge fissa il testo canonico lato server,
 * poi si firma quel testo. Firmare qualcosa deciso dal client sarebbe una firma su nulla.
 */
export async function submitLegalAttestation({
  apiBaseUrl = 'https://api.elitefitness.it',
  authToken,
  acceptances,
}) {
  if (!authToken) throw new Error('Sessione non disponibile per la firma delle condizioni.');

  const base = apiBaseUrl.replace(/\/$/, '');
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  const chRes = await fetch(`${base}/api/legal/attestation/challenge`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ acceptances }),
  });
  if (!chRes.ok) {
    const t = await chRes.text();
    throw new Error(t?.slice(0, 200) || `Errore challenge attestazione (${chRes.status})`);
  }
  const ch = await chRes.json();

  // Ricalcolo locale dello SHA: se il canonico che sto per firmare non corrisponde al digest che il
  // server dice di aver conservato, la firma sarebbe su un testo diverso da quello archiviato.
  await assertSha256Utf8Matches(ch.canonicalPayload, ch.contentSha256);

  const keys = await getOrCreateRegistrationSigningKeyPair();
  const sigBuf = await signCanonicalPayloadUtf8(keys.privateKey, ch.canonicalPayload);

  const res = await fetch(`${base}/api/legal/attestation`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      commitmentId: ch.commitmentId,
      acceptances,
      publicKey: await exportPublicJwk(keys.publicKey),
      signatureBase64: signatureDerToStandardBase64(sigBuf),
    }),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t?.slice(0, 200) || `Errore invio attestazione (${res.status})`);
  }
  return res.json();
}
